import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { IonIcon } from '@ionic/react';
import { searchOutline, close } from 'ionicons/icons';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
  className?: string;
}

/** Strip diacritics for Romanian-friendly matching (ă→a, ț→t, etc.) */
function normalize(str: string): string {
  return str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Caută...',
  emptyLabel = 'Niciuna',
  disabled = false,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0, openAbove: false });

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedOption = options.find((o) => o.value === value);
  const displayLabel = selectedOption ? selectedOption.label : emptyLabel;

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = normalize(search);
    return options.filter((o) => normalize(o.label).includes(q));
  }, [options, search]);

  const updateDropdownPos = useCallback(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const maxH = 280;
    const openAbove = spaceBelow < maxH + 8;
    setDropdownPos({
      top: openAbove ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      openAbove,
    });
  }, []);

  const openDropdown = useCallback(() => {
    if (disabled) return;
    setSearch('');
    setHighlightIndex(-1);
    updateDropdownPos();
    setIsOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [disabled, updateDropdownPos]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearch('');
  }, []);

  const handleSelect = useCallback((val: string | null) => {
    onChange(val);
    setIsOpen(false);
    setSearch('');
  }, [onChange]);

  // Close on outside click or scroll-reposition
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current && !wrapperRef.current.contains(target)) {
        const portal = document.getElementById('ss-dropdown-portal');
        if (portal && portal.contains(target)) return;
        closeDropdown();
      }
    };
    const handleScroll = () => updateDropdownPos();
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, updateDropdownPos, closeDropdown]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (!listRef.current || highlightIndex < 0) return;
    const el = listRef.current.children[highlightIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const total = filtered.length + 1; // +1 for empty option
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, total - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIndex <= 0) {
        handleSelect(null);
      } else {
        const opt = filtered[highlightIndex - 1];
        if (opt) handleSelect(opt.value);
      }
    } else if (e.key === 'Escape') {
      closeDropdown();
    }
  }, [filtered, highlightIndex, handleSelect, closeDropdown]);

  const dropdown = isOpen
    ? createPortal(
        <div
          id="ss-dropdown-portal"
          className="ss-dropdown"
          style={{
            position: 'fixed',
            zIndex: 99999,
            ...(dropdownPos.openAbove
              ? { bottom: window.innerHeight - dropdownPos.top, top: 'auto' }
              : { top: dropdownPos.top }),
            left: dropdownPos.left,
            width: dropdownPos.width,
          }}
        >
          <div className="ss-dropdown__search">
            <IonIcon icon={searchOutline} className="ss-dropdown__search-icon" />
            <input
              ref={inputRef}
              type="text"
              className="ss-dropdown__search-input"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setHighlightIndex(-1); }}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              autoComplete="off"
            />
            {search ? (
              <button type="button" className="ss-dropdown__search-clear" onClick={() => setSearch('')}>
                <IonIcon icon={close} />
              </button>
            ) : null}
          </div>
          <ul ref={listRef} className="ss-dropdown__list">
            <li
              className={`ss-dropdown__item${value == null || value === '' ? ' ss-dropdown__item--selected' : ''}${highlightIndex === 0 ? ' ss-dropdown__item--active' : ''}`}
              onMouseEnter={() => setHighlightIndex(0)}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(null); }}
            >
              <span className="ss-dropdown__item-text ss-dropdown__item-text--empty">{emptyLabel}</span>
            </li>
            {filtered.map((opt, idx) => (
              <li
                key={opt.value}
                className={`ss-dropdown__item${opt.value === value ? ' ss-dropdown__item--selected' : ''}${highlightIndex === idx + 1 ? ' ss-dropdown__item--active' : ''}`}
                onMouseEnter={() => setHighlightIndex(idx + 1)}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(opt.value); }}
              >
                <span className="ss-dropdown__item-text">{opt.label}</span>
              </li>
            ))}
            {filtered.length === 0 && search ? (
              <li className="ss-dropdown__empty">Niciun rezultat pentru „{search}"</li>
            ) : null}
          </ul>
        </div>,
        document.body,
      )
    : null;

  return (
    <div ref={wrapperRef} className={`ss-wrapper${className ? ` ${className}` : ''}`}>
      <button
        type="button"
        className={`form-input form-select ss-trigger${isOpen ? ' ss-trigger--open' : ''}${disabled ? ' ss-trigger--disabled' : ''}`}
        onClick={openDropdown}
        disabled={disabled}
      >
        <span className={`ss-trigger__label${!value ? ' ss-trigger__label--placeholder' : ''}`}>
          {displayLabel}
        </span>
      </button>
      {value && !disabled ? (
        <button
          type="button"
          className="ss-clear"
          onClick={(e) => { e.stopPropagation(); handleSelect(null); }}
        >
          <IonIcon icon={close} />
        </button>
      ) : null}
      {dropdown}
    </div>
  );
};

export default SearchableSelect;
